import { FC } from 'react';
import { DataGrid, GridActionsCellItem, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Box, Tooltip } from '@mui/material';
import dayjs from 'dayjs';

import { TrblDeleteIcon, TrblLockResetIcon } from '@/components/Icons';

import {
  formatDate,
  getKeyServerName,
  getKeyStatus,
  getOwnerName,
  GRID_FOCUS_SX,
  KeyRow,
  LIBRARY_SCOPES,
  SDK_DOCS_SCOPE,
} from '../constants';

import adminStyles from '../../AdministrationPage/styles.module.scss';
import styles from '../styles.module.scss';

type KeysGridProps = {
  keys: KeyRow[];
  showOwner: boolean;
  isLoading: boolean;
  /** Only the owner can rotate a key, since rotating shows its new secret. */
  canRotate: boolean;
  onRotate: (key: KeyRow) => void;
  onRevoke: (key: KeyRow) => void;
};

export const ScopeChips: FC<{ scopes: string[] }> = ({ scopes }) => {
  const libraries = LIBRARY_SCOPES.filter(({ scope }) => scopes.includes(scope));
  return (
    <div className={styles['chips']}>
      {scopes.includes(SDK_DOCS_SCOPE) && <span className={`${styles['chip']} ${styles['chip-docs']}`}>Docs</span>}
      {libraries.length === LIBRARY_SCOPES.length ? (
        <span className={styles['chip']}>All libraries</span>
      ) : (
        libraries.map(({ scope, short }) => (
          <span key={scope} className={styles['chip']}>
            {short}
          </span>
        ))
      )}
    </div>
  );
};

export const KeysGrid: FC<KeysGridProps> = ({ keys, showOwner, isLoading, canRotate, onRotate, onRevoke }) => {
  const columns: GridColDef<KeyRow>[] = [
    ...(showOwner
      ? [
          {
            field: 'owner',
            headerName: 'Owner',
            width: 220,
            valueGetter: (params: { row: KeyRow }) => getOwnerName(params.row),
            renderCell: (params: GridRenderCellParams<KeyRow>) => (
              <Tooltip title={params.row.ownerEmail ?? ''}>
                <span>{getOwnerName(params.row)}</span>
              </Tooltip>
            ),
            headerClassName: adminStyles['data-grid-header'],
          } satisfies GridColDef<KeyRow>,
        ]
      : []),
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 180, headerClassName: adminStyles['data-grid-header'] },
    {
      field: 'keyPrefix',
      headerName: 'Key',
      width: 140,
      renderCell: (params: GridRenderCellParams<KeyRow>) => (
        <code className={styles['prefix']}>{params.row.keyPrefix}…</code>
      ),
      headerClassName: adminStyles['data-grid-header'],
    },
    {
      field: 'server',
      headerName: 'Server',
      width: 170,
      valueGetter: (params) => getKeyServerName(params.row),
      headerClassName: adminStyles['data-grid-header'],
    },
    {
      field: 'scopes',
      headerName: 'Access',
      width: 260,
      sortable: false,
      renderCell: (params: GridRenderCellParams<KeyRow>) => <ScopeChips scopes={params.row.scopes} />,
      headerClassName: adminStyles['data-grid-header'],
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 110,
      valueFormatter: (params) => formatDate(params.value),
      headerClassName: adminStyles['data-grid-header'],
    },
    {
      field: 'lastUsedAt',
      headerName: 'Last used',
      width: 140,
      valueFormatter: (params) => (params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm') : 'Never'),
      headerClassName: adminStyles['data-grid-header'],
    },
    {
      field: 'expiresAt',
      headerName: 'Expires',
      width: 110,
      valueFormatter: (params) => formatDate(params.value),
      headerClassName: adminStyles['data-grid-header'],
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      valueGetter: (params) => getKeyStatus(params.row),
      renderCell: (params: GridRenderCellParams<KeyRow, string>) => (
        <span className={[styles['status'], styles['status-' + params.value?.split(' ')[0].toLowerCase()]].join(' ')}>
          {params.value}
        </span>
      ),
      headerClassName: adminStyles['data-grid-header'],
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<KeyRow>) =>
        params.row.revokedAt ? null : (
          <div className={adminStyles['row-actions-cell']}>
            {canRotate && (
              <Tooltip title="Rotate key">
                <GridActionsCellItem icon={<TrblLockResetIcon />} label="Rotate" onClick={() => onRotate(params.row)} />
              </Tooltip>
            )}
            <Tooltip title="Revoke key">
              <GridActionsCellItem icon={<TrblDeleteIcon />} label="Revoke" onClick={() => onRevoke(params.row)} />
            </Tooltip>
          </div>
        ),
      headerClassName: adminStyles['data-grid-header'],
    },
  ];

  return (
    <Box component="div" height="100%" width="100%">
      <DataGrid
        sx={{
          ...GRID_FOCUS_SX,
          '& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator': { display: 'none' },
        }}
        rows={keys}
        loading={isLoading}
        columns={columns}
        autoPageSize
        disableRowSelectionOnClick
        disableColumnSelector
        rowHeight={42}
        columnHeaderHeight={42}
        localeText={{ noRowsLabel: 'No MCP API keys' }}
        slotProps={{ row: { className: adminStyles['data-grid-row'] } }}
      />
    </Box>
  );
};
